/**
 * Column Menu Hooks - StreamLain Extension System
 *
 * Allows StreamLain to register additional menu items for database column headers
 * without modifying BlockSuite source files.
 *
 * This uses a window global to avoid circular dependencies between
 * BlockSuite (library) and Frontend (application).
 */
import { I18n } from '@affine/i18n';
import type { DatabaseBlockDataSource } from '@blocksuite/affine/blocks/database';
import {
  menu,
  type MenuConfig,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import type {
  Property,
  SingleView,
  StreamlainColumnMenuHook,
} from '@blocksuite/data-view';
import { html } from 'lit/static-html.js';

import { resolveRelationEndpointAccess } from '../../database-block/properties/relation/access-state';
import {
  duplicateRelationColumn,
  type RelationDuplicateDeps,
  type RelationPairDialogResult,
  trackRelationPairDialog,
} from '../../database-block/properties/relation/duplicate-relation-column';
import type { RelationConfigMenu } from '../../database-block/properties/relation/relation-config-menu';
import { deleteInverseRelationUnified } from '../../database-block/properties/relation/unified-relation-service';

export type ColumnMenuHook = StreamlainColumnMenuHook;

/**
 * Register a column menu hook (via window global)
 */
export function registerColumnMenuHook(hook: ColumnMenuHook): void {
  if (!window.__streamlainColumnMenuHooks) {
    window.__streamlainColumnMenuHooks = [];
  }
  window.__streamlainColumnMenuHooks.push(hook);
}

// ============================================================================
// StreamLain Menu Hooks Registration
// ============================================================================

const RELATION_CONFIG_SUBMENU_CLASS = 'streamlain-relation-config-submenu';

/**
 * Relation Config — submenu variant (rendered inside the column-header menu).
 * Uses the Lit <relation-config-menu> element as a submenu overlay item.
 */
function createRelationConfigMenu(
  column: Property,
  tableViewManager: SingleView,
  onSaveStarted?: RelationConfigMenu['onSaveStarted']
): MenuConfig {
  return menuInstance => {
    const dataSource = tableViewManager.manager
      .dataSource as DatabaseBlockDataSource;
    const store = dataSource.doc;
    const currentDatabaseId = dataSource.blockId ?? '';

    return html`
      <relation-config-menu
        .store="${store}"
        .currentDatabaseId="${currentDatabaseId}"
        .propertyId="${column.id}"
        .data="${column.data$.value}"
        .sourceColumnName="${column.name$.value}"
        .sourceColumnNames="${dataSource
          .getAllPropertyIds()
          .filter(id => id !== column.id)
          .map(id => dataSource.propertyNameGet(id))}"
        .onPreviewSourceColumnName="${(name: string) =>
          previewOpenColumnMenuNameField(column, name)}"
        .onCommitSourceColumnName="${(name: string) => column.nameSet(name)}"
        .onUpdate="${(data: Record<string, unknown>) => {
          column.dataUpdate(() => data);
        }}"
        .onClose="${() => menuInstance.close()}"
        .onSaveStarted="${onSaveStarted}"
      ></relation-config-menu>
    `;
  };
}

/**
 * Auto-expand the "Relation Config" submenu inside the still-open
 * column-header menu. After the user picks Type=Relation from the Type
 * submenu, BlockSuite closes the Type submenu and returns focus to the
 * main column menu. We wait for the main menu's DOM to update (the
 * Relation Config row is `hide`-gated on `type$ === 'relation'`), then
 * click its submenu button — same code path as a real user click, so
 * positioning / theming / focus all stay native.
 */
function expandRelationConfigSubMenu(): boolean {
  // BlockSuite renders nested menus as siblings; the last <affine-menu>
  // in DOM order is the main (column-header) menu now that the Type
  // submenu has unmounted.
  const menus = document.querySelectorAll('affine-menu');
  const mainMenu = menus[menus.length - 1];
  if (!mainMenu) return false;
  const subMenu = mainMenu
    .querySelector(`.${RELATION_CONFIG_SUBMENU_CLASS}`)
    ?.closest<HTMLElement>('affine-menu-sub-menu');
  if (!subMenu) return false;
  subMenu.click();
  return true;
}

/**
 * Opens the relation settings as a standalone popup, e.g. from the column
 * header after a type change or from the configure button of an
 * unconfigured relation cell.
 */
export function popRelationConfigMenu(
  column: Property,
  tableViewManager: SingleView,
  anchor: HTMLElement
): void {
  popColumnConfigMenu(
    createRelationConfigMenu(column, tableViewManager),
    anchor
  );
}

/**
 * Opens the menu of column `columnId` in the database block `blockId`, as a
 * click on its header does, and for a relation also its relation settings.
 * Resolves whether the relation settings opened.
 */
export async function openColumnMenu(
  blockId: string,
  columnId: string
): Promise<boolean> {
  const block = document.querySelector<HTMLElement>(
    `[data-block-id="${CSS.escape(blockId)}"]`
  );
  const virtualHeader = block?.querySelector<
    HTMLElement & { openPropertyMenuById: (id: string) => Promise<void> }
  >('virtual-table-header');
  if (virtualHeader) {
    await virtualHeader.openPropertyMenuById(columnId);
  } else {
    const header = block?.querySelector<
      HTMLElement & { editTitle: () => void }
    >(
      `affine-database-header-column[data-column-id="${CSS.escape(columnId)}"]`
    );
    if (!header) return false;
    header.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    header.editTitle();
  }
  // The column menu renders its relation row on the next frames.
  await new Promise<void>(resolve =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  );
  return expandRelationConfigSubMenu();
}

function popColumnConfigMenu(
  config: MenuConfig,
  anchor: HTMLElement,
  onClose?: () => void
): void {
  popMenu(popupTargetFromElement(anchor), {
    options: {
      preferDesktop: true,
      items: [config],
      onClose,
    },
    placement: 'right-start',
  });
}

function previewOpenColumnMenuNameField(column: Property, name: string): void {
  const currentName = column.name$.value;
  for (const menuElement of Array.from(
    document.querySelectorAll('affine-menu')
  )) {
    const input = menuElement.querySelector<HTMLInputElement>(
      'affine-menu-input input'
    );
    if (!input) continue;
    if (input.value !== currentName && input.value !== 'Relation') continue;
    input.value = name;
    return;
  }
}

/**
 * Rollup Config Menu (E018-S008-T009)
 */
function createRollupConfigMenu(
  column: Property,
  tableViewManager: SingleView
): MenuConfig {
  return menuInstance => {
    const dataSource = tableViewManager.manager
      .dataSource as DatabaseBlockDataSource;
    const store = dataSource.doc;
    const currentDatabaseId = dataSource.blockId ?? '';

    return html`
      <rollup-config-menu
        .store="${store}"
        .currentDatabaseId="${currentDatabaseId}"
        .propertyId="${column.id}"
        .data="${column.data$.value}"
        .onUpdate="${(data: Record<string, unknown>) => {
          column.dataUpdate(() => data);
        }}"
        .onClose="${() => menuInstance.close()}"
      ></rollup-config-menu>
    `;
  };
}

/** Opens the same rollup configuration used by the column header. */
export function popRollupConfigMenu(
  column: Property,
  tableViewManager: SingleView,
  anchor: HTMLElement
): void {
  if (column.readonly$.value) return;
  popColumnConfigMenu(createRollupConfigMenu(column, tableViewManager), anchor);
}

function confirmRelationDuplicate(
  title: string,
  description: string,
  confirmText: string,
  variant: 'primary' | 'error' = 'primary'
): Promise<boolean> {
  const modal = window.__confirmModal;
  if (!modal) {
    console.warn('[RelationDuplicate] __confirmModal bridge not available');
    return Promise.resolve(false);
  }
  return new Promise<boolean>(resolve => {
    modal.openConfirmModal({
      title,
      description,
      confirmText,
      cancelText: I18n['com.streamlain.confirmModal.button.cancel'](),
      confirmButtonOptions: { variant },
      onConfirm: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
}

const RELATION_DUPLICATE_REFUSALS = {
  'target-read-only': {
    title: () =>
      I18n['com.streamlain.streamlain.relation.target-read-only-title'](),
    description: () =>
      I18n[
        'com.streamlain.streamlain.relation.duplicate.target-read-only-description'
      ](),
  },
  'target-missing': {
    title: () =>
      I18n[
        'com.streamlain.streamlain.relation.duplicate.target-missing-title'
      ](),
    description: () =>
      I18n[
        'com.streamlain.streamlain.relation.duplicate.target-missing-description'
      ](),
  },
  'target-trashed': {
    title: () =>
      I18n[
        'com.streamlain.streamlain.relation.duplicate.target-trashed-title'
      ](),
    description: (database: string) =>
      I18n[
        'com.streamlain.streamlain.relation.duplicate.target-trashed-description'
      ]({ database }),
  },
  'target-not-loaded': {
    title: () =>
      I18n[
        'com.streamlain.streamlain.relation.duplicate.target-not-loaded-title'
      ](),
    description: (database: string) =>
      I18n[
        'com.streamlain.streamlain.relation.duplicate.target-not-loaded-description'
      ]({ database }),
  },
} as const;

/** The rendered header of a column, or the database block without one. */
function columnAnchor(blockId: string, columnId: string): HTMLElement | null {
  const block = document.querySelector<HTMLElement>(
    `[data-block-id="${CSS.escape(blockId)}"]`
  );
  return (
    block?.querySelector<HTMLElement>(
      `:is(virtual-database-header-column, affine-database-header-column)[data-column-id="${CSS.escape(columnId)}"]`
    ) ?? block
  );
}

/**
 * Opens the relation settings for the copy next to the original, the same
 * dialog that creates every relation with its backlink. Resolves when the
 * dialog closed and every save it started has finished.
 */
async function configureRelationPair(
  view: SingleView,
  copyId: string,
  originalId: string
): Promise<RelationPairDialogResult> {
  const copy = view.propertyGetOrCreate(copyId);
  copy.move({ before: false, id: originalId });
  const dataSource = view.manager.dataSource as DatabaseBlockDataSource;
  await dataSource.flushLocalWrites();
  // The header renders the copy on the next frames.
  await new Promise<void>(resolve =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  );
  const anchor = columnAnchor(dataSource.blockId, copyId);
  if (!anchor) {
    throw new Error(
      I18n['com.streamlain.streamlain.relation.config.inverse-create-failed']()
    );
  }
  const dialog = trackRelationPairDialog();
  return new Promise<RelationPairDialogResult>(resolve => {
    popColumnConfigMenu(
      createRelationConfigMenu(copy, view, dialog.saveStarted),
      anchor,
      () => resolve(dialog.closed())
    );
  });
}

function relationDuplicateDeps(
  view: SingleView,
  dataSource: DatabaseBlockDataSource
): RelationDuplicateDeps {
  return {
    resolveAccess: resolveRelationEndpointAccess,
    configurePair: (copyId, originalId) =>
      configureRelationPair(view, copyId, originalId),
    removeBacklink: async backlink => {
      const store = dataSource.doc;
      const removed = await deleteInverseRelationUnified({
        store,
        currentDocId: store.id,
        targetDocId: backlink.targetDocId,
        targetDatabaseId: backlink.targetDatabaseId,
        inverseFieldId: backlink.backlinkId,
      });
      if (!removed.success) throw removed.error ?? new Error('backlink kept');
    },
    report: (outcome, { database, error }) => {
      const ok = I18n['com.streamlain.confirmModal.button.ok']();
      if (outcome !== 'failed') {
        const refusal = RELATION_DUPLICATE_REFUSALS[outcome];
        void confirmRelationDuplicate(
          refusal.title(),
          refusal.description(database),
          ok
        );
        return;
      }
      void confirmRelationDuplicate(
        I18n['com.streamlain.streamlain.relation.duplicate.failed-title'](),
        error?.message ??
          I18n[
            'com.streamlain.streamlain.relation.config.inverse-create-failed'
          ](),
        ok,
        'error'
      );
    },
  };
}

/**
 * Initialize StreamLain column menu hooks
 * Call this during app initialization
 */
export function initColumnMenuHooks(): void {
  // Relation Config
  registerColumnMenuHook({
    name: 'Relation Config',
    getSubMenu: (column, tableViewManager) =>
      menu.subMenu({
        name: I18n['com.streamlain.streamlain.relation.config.title'](),
        class: RELATION_CONFIG_SUBMENU_CLASS,
        hide: () => !column.dataUpdate || column.type$.value !== 'relation',
        options: {
          items: [createRelationConfigMenu(column, tableViewManager)],
        },
      }),
    duplicate: (column, tableViewManager) => {
      if (column.type$.value !== 'relation') return false;
      const dataSource = tableViewManager.manager
        .dataSource as DatabaseBlockDataSource;
      duplicateRelationColumn(
        dataSource,
        column.id,
        relationDuplicateDeps(tableViewManager, dataSource)
      ).catch(error => {
        console.error('[RelationDuplicate] failed', error);
      });
      return true;
    },
    onAfterTypeChange: (column, tableViewManager, newType, anchor) => {
      if (newType !== 'relation') return;
      // Wait past: (a) Type submenu close animation, (b) main menu re-render
      // after column.type$ flips to 'relation' so the Relation Config row
      // stops being `hide`-gated. Two rAFs is enough in practice.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (expandRelationConfigSubMenu()) return;
          popRelationConfigMenu(column, tableViewManager, anchor);
        })
      );
    },
  });

  // Rollup Config
  registerColumnMenuHook({
    name: 'Rollup Config',
    getSubMenu: (column, tableViewManager) =>
      menu.subMenu({
        name: 'Rollup Config',
        hide: () => !column.dataUpdate || column.type$.value !== 'rollup',
        options: {
          items: [createRollupConfigMenu(column, tableViewManager)],
        },
      }),
  });
}

// Auto-initialize when this module is imported
initColumnMenuHooks();
